import { useTranslation } from 'react-i18next';

import type { SerieTermRow, TermEconomicsItem } from '@/api/generated/schemas';
import { cn } from '@/shared/lib/utils';
import { Button, Icon, Skeleton, Table, TableCell, TableHeadCell, TableRow } from '@/shared/ui';
import { CapacityBar } from '../../../CapacityBar';
import { formatInteger, formatOneDecimal, formatTermDate } from '../serieFormat';
import { SerieStateBadge } from './serieStateBadge';

interface SerieTermsTableProps {
  terms: SerieTermRow[];
  economicsByTerm: Map<string, TermEconomicsItem>;
  currency: string;
  isEconomicsLoading: boolean;
  /** Otevře detail termínu (proklik „open“ na řádku). */
  onOpenTerm: (termGroupId: string) => void;
}

function Muted() {
  return <span className="text-phx-text-muted">—</span>;
}

/**
 * Tabulka termínů seriálu (dense). Ekonomické sloupce (Kapacita, Cena/pax, Marže) ukazují skeleton, dokud nedoběhne
 * samostatný ekonomický request; stavové sloupce a datum jsou dostupné okamžitě.
 */
export function SerieTermsTable({
  terms,
  economicsByTerm,
  currency,
  isEconomicsLoading,
  onOpenTerm,
}: SerieTermsTableProps) {
  const { t } = useTranslation('venua-planner');

  return (
    <Table density="dense">
      <Table.Head>
        <TableRow>
          {/* Úvodní sloupec drží jen tlačítko „otevřít detail“ — stejně jako v matici dopravy. */}
          <TableHeadCell density="dense" className="w-8" />
          <TableHeadCell density="dense">{t('serieDetail.terms.col.code')}</TableHeadCell>
          <TableHeadCell density="dense">{t('serieDetail.terms.col.date')}</TableHeadCell>
          <TableHeadCell density="dense">{t('serieDetail.terms.col.capacity')}</TableHeadCell>
          <TableHeadCell density="dense">{t('serieDetail.terms.col.termState')}</TableHeadCell>
          <TableHeadCell density="dense">{t('serieDetail.terms.col.operation')}</TableHeadCell>
          <TableHeadCell density="dense">{t('serieDetail.terms.col.productCost')}</TableHeadCell>
          <TableHeadCell density="dense">{t('serieDetail.terms.col.calculation')}</TableHeadCell>
          <TableHeadCell density="dense" className="text-right">
            {t('serieDetail.terms.col.pricePerPax')}
          </TableHeadCell>
          <TableHeadCell density="dense" className="text-right">
            {t('serieDetail.terms.col.margin')}
          </TableHeadCell>
        </TableRow>
      </Table.Head>
      <Table.Body>
        {terms.map((term) => {
          const economics = economicsByTerm.get(term.termGroupId);
          const loadingEconomics = isEconomicsLoading || !economics;
          return (
            <TableRow key={term.termGroupId}>
              <TableCell density="dense">
                <Button
                  size="icon-sm"
                  variant="ghost"
                  title={t('serieDetail.terms.openTerm')}
                  onClick={() => onOpenTerm(term.termGroupId)}
                >
                  <Icon name="open" size={13} />
                </Button>
              </TableCell>
              <TableCell density="dense">
                <span
                  className={cn(
                    'text-phx-sm font-mono',
                    term.isCancelled && 'text-phx-text-muted line-through'
                  )}
                >
                  {term.code}
                </span>
              </TableCell>
              <TableCell density="dense" className="text-phx-text-muted whitespace-nowrap">
                {formatTermDate(term.dateFrom, term.dateTo)}
                {term.nights != null ? (
                  <span className="ml-1 opacity-70">
                    {' '}
                    · {t('serieDetail.terms.nights', { count: term.nights })}
                  </span>
                ) : null}
              </TableCell>
              <TableCell density="dense">
                {loadingEconomics ? (
                  <Skeleton width={74} height={9} />
                ) : (
                  <CapacityBar used={economics.usedCapacity} capacity={economics.capacity} />
                )}
              </TableCell>
              <TableCell density="dense">
                <SerieStateBadge state={term.termState} />
              </TableCell>
              <TableCell density="dense">
                <SerieStateBadge state={term.operationState} />
              </TableCell>
              <TableCell density="dense">
                <SerieStateBadge state={term.productCostState} />
              </TableCell>
              <TableCell density="dense">
                <SerieStateBadge state={term.calculationState} />
              </TableCell>
              <TableCell density="dense" className="text-right tabular-nums">
                {loadingEconomics ? (
                  <Skeleton width={48} />
                ) : economics.pricePerPax == null ? (
                  <Muted />
                ) : (
                  `${formatInteger(economics.pricePerPax)} ${currency}`
                )}
              </TableCell>
              <TableCell density="dense" className="text-right tabular-nums">
                {loadingEconomics ? (
                  <Skeleton width={40} />
                ) : economics.marginPercent == null ? (
                  <Muted />
                ) : (
                  `${formatOneDecimal(economics.marginPercent)} %`
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </Table.Body>
    </Table>
  );
}
